/**
 * MASTERCASE — Checkout Redirect (v2 — hardened)
 *
 * Intercepts ALL paths to Shopify's native checkout and redirects
 * to pay.getmastercase.com with the full cart payload via POST.
 *
 * Protected vectors:
 *  1. Submit on cart form (button name="checkout")
 *  2. Submit on cart form with null submitter (Enter key)
 *  3. Click on any <a href="/checkout"> link
 *  4. Programmatic navigation to /checkout (location hijack)
 *  5. beforeunload safety net
 *
 * Properties prefixed with _ (like _image_file) are INCLUDED — they
 * contain CDN URLs needed for draft order creation.
 */
(function () {
  'use strict';

  var CHECKOUT_URL = 'https://pay.getmastercase.com/';
  var redirecting = false;

  function postToCheckout(items, total, currency) {
    var form = document.createElement('form');
    form.method = 'POST';
    form.action = CHECKOUT_URL;
    form.style.display = 'none';

    [
      ['items', JSON.stringify(items)],
      ['total', total.toString()],
      ['currency', currency]
    ].forEach(function (pair) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = pair[0];
      input.value = pair[1];
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }

  /* ── Build payload and redirect ── */
  function doRedirect() {
    if (redirecting) return;
    redirecting = true;

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.items || cart.items.length === 0) {
          redirecting = false;
          return;
        }

        var items = cart.items.map(function (item) {
          var props = {};
          if (item.properties) {
            for (var key in item.properties) {
              if (item.properties.hasOwnProperty(key) && item.properties[key] !== null && item.properties[key] !== '') {
                props[key] = item.properties[key];
              }
            }
          }
          return {
            variant_id: item.variant_id,
            title: item.product_title,
            variant_title: item.variant_title,
            price: item.final_price / 100,
            quantity: item.quantity,
            image: item.featured_image ? item.featured_image.url : '',
            properties: props
          };
        });

        var total = cart.total_price / 100;
        var currency = cart.currency || 'XOF';

        postToCheckout(items, total, currency);
      })
      .catch(function (err) {
        console.error('[MC Checkout]', err);
        redirecting = false;
        alert('Erreur lors du chargement du panier. Veuillez réessayer.');
      });
  }

  /* ── 1. Intercept ALL submits on the cart form ──
   * Catches: checkout button click, Enter key (null submitter),
   * and any programmatic form.submit() */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    /* Cart form submit with checkout button */
    if (e.submitter && e.submitter.name === 'checkout') {
      e.preventDefault();
      doRedirect();
      return;
    }
    /* Cart form submit without explicit submitter (Enter key, etc.)
     * Only intercept if it's the actual cart form */
    if (form.id === 'cart-form' || form.action && form.action.indexOf('/cart') !== -1) {
      /* Check if there's a checkout button in this form */
      var hasCheckoutBtn = form.querySelector('[name="checkout"]') ||
                           document.querySelector('[name="checkout"][form="' + form.id + '"]');
      if (hasCheckoutBtn) {
        e.preventDefault();
        doRedirect();
        return;
      }
    }
  }, true);

  /* ── 2. Intercept clicks on checkout links ── */
  document.addEventListener('click', function (e) {
    var a = e.target.closest('a[href*="/checkout"]');
    if (a) {
      e.preventDefault();
      doRedirect();
      return;
    }
    /* Also catch buttons/elements with checkout-related data attributes
     * (covers dynamic checkout buttons injected by apps) */
    var btn = e.target.closest('[data-shopify-checkout], [data-checkout-url]');
    if (btn) {
      e.preventDefault();
      doRedirect();
    }
  }, true);

  /* ── 3. Hijack location setters to catch programmatic /checkout navigation ── */
  var locationDescriptor = Object.getOwnPropertyDescriptor(window, 'location');

  /* Only wrap if we can (some browsers protect window.location) */
  if (locationDescriptor && locationDescriptor.configurable !== false) {
    try {
      var _assign = window.location.assign.bind(window.location);
      var _replace = window.location.replace.bind(window.location);

      window.location.assign = function (url) {
        if (typeof url === 'string' && url.indexOf('/checkout') !== -1 && url.indexOf(CHECKOUT_URL) === -1) {
          doRedirect();
          return;
        }
        return _assign(url);
      };

      window.location.replace = function (url) {
        if (typeof url === 'string' && url.indexOf('/checkout') !== -1 && url.indexOf(CHECKOUT_URL) === -1) {
          doRedirect();
          return;
        }
        return _replace(url);
      };
    } catch (ex) {
      /* location methods not wrappable — fall through to beforeunload guard */
    }
  }

  /* ── 4. beforeunload safety net ──
   * Last resort: if somehow a navigation to /checkout slips through,
   * catch it and redirect. This won't block the navigation but logs it. */
  window.addEventListener('beforeunload', function () {
    var dest = window.location.href;
    if (dest.indexOf('/checkout') !== -1 && dest.indexOf(CHECKOUT_URL) === -1 && !redirecting) {
      /* We can't reliably redirect here, but we flag it.
       * The primary protections above should catch everything. */
      console.warn('[MC Checkout] Unintercepted checkout navigation detected:', dest);
    }
  });

  /* ── 5. Periodic DOM scan for injected checkout elements ──
   * Catches: third-party apps that inject checkout buttons after page load */
  function patchDynamicCheckoutButtons() {
    var buttons = document.querySelectorAll(
      'a[href*="/checkout"]:not([data-mc-patched]), ' +
      'form[action*="/checkout"]:not([data-mc-patched])'
    );
    buttons.forEach(function (el) {
      el.setAttribute('data-mc-patched', 'true');
      if (el.tagName === 'A') {
        el.addEventListener('click', function (e) {
          e.preventDefault();
          doRedirect();
        });
      } else if (el.tagName === 'FORM') {
        el.addEventListener('submit', function (e) {
          e.preventDefault();
          doRedirect();
        });
      }
    });
  }

  /* Run once on load, then observe for new elements */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', patchDynamicCheckoutButtons);
  } else {
    patchDynamicCheckoutButtons();
  }

  var observer = new MutationObserver(function () {
    patchDynamicCheckoutButtons();
  });
  observer.observe(document.body || document.documentElement, {
    childList: true,
    subtree: true
  });
})();
